<script lang="ts">
import SuperAdminLayout from "@/layouts/SuperAdminLayout.vue"

export default { layout: SuperAdminLayout }
</script>

<script setup lang="ts">
import { Head } from "@inertiajs/vue3"
import { SbList, SbListItem, SbListLabel, SbListSection } from "@unio/saltbox-ui"

import DetailRow from "@/components/DetailRow.vue"
import FlashAlerts from "@/components/FlashAlerts.vue"
import LabeledSection from "@/components/LabeledSection.vue"
import RoleBadge from "@/components/RoleBadge.vue"
import { formatDate } from "@/lib/format"
import { superAdminUserPath } from "@/routes"

export interface MemberEntry {
  id: string
  role: "owner" | "member"
  user_name: string
  user_email: string
  user_initials: string
  user_id: string
  avatar_url: string | null
  deactivated: boolean
}

const props = defineProps<{
  account: {
    id: string
    name: string
    items_count: number
    created_at: string
    owners: string
    members: MemberEntry[]
  }
}>()
</script>

<template>
  <Head :title="`${account.name} — Admin`" />

  <div class="flex flex-col gap-6">
    <FlashAlerts />

    <SbList bordered separator>
      <DetailRow label="Owner" :value="account.owners" />
      <DetailRow label="Items" :value="String(account.items_count)" />
      <DetailRow label="Created" :value="formatDate(account.created_at)" />
    </SbList>

    <LabeledSection v-if="account.members.length" title="Members">
      <SbListItem
        v-for="member in account.members"
        :key="member.id"
        :to="superAdminUserPath(member.user_id)"
      >
        <SbListSection avatar>
          <UAvatar
            :src="member.avatar_url ?? undefined"
            :text="member.user_initials"
            :alt="member.user_name"
            size="sm"
          />
        </SbListSection>
        <SbListSection>
          <SbListLabel :class="{ 'text-muted': member.deactivated }">
            {{ member.user_name }}
          </SbListLabel>
          <SbListLabel caption>{{ member.user_email }}</SbListLabel>
        </SbListSection>
        <SbListSection side>
          <div class="flex items-center gap-2">
            <UBadge v-if="member.deactivated" color="neutral" variant="outline">
              Deactivated
            </UBadge>
            <RoleBadge :role="member.role" />
          </div>
        </SbListSection>
      </SbListItem>
    </LabeledSection>
  </div>
</template>
